'use client';
import { FC, useState } from 'react';
import { Box, Button, Stack, Typography, Chip, CircularProgress } from '@mui/material';
import { Edit, Save as SaveIcon } from '@mui/icons-material';
import { formatDate } from '@/lib/utils/dateUtils';
import { useAuthStore } from '@/stores/authStore';
import { PrivilegeEnum } from '@/api/ApiClient';
import { PROJECT_STATUS_LABELS, isProjectStatusDto } from '@/lib/types/projectTypes';

interface ProjectFooterProps {
  lastChangedDate?: Date;
  lastChangedBy: string;
  projectStatus: string;
  projectStatusDisplay: string;
  isReadOnly: boolean;
  hasChanges: boolean;
  onSave: () => void | Promise<void>;
  onDelete: () => void;
  onCancel: () => void;
  setIsReadOnly: (val: boolean) => void;
  privilege: PrivilegeEnum;
  isFormValid?: boolean;
  validationIssues?: string[];
  /** When set, Delete is disabled and this explains why (workflow vs lifecycle rules). */
  deleteDisabledReason?: string;
}

const ProjectFooter: FC<ProjectFooterProps> = ({
  lastChangedDate,
  lastChangedBy,
  projectStatus,
  isReadOnly,
  hasChanges,
  onSave,
  onDelete,
  onCancel,
  setIsReadOnly,
  privilege,
  isFormValid = true,
  validationIssues = [],
  deleteDisabledReason,
}) => {
  const { isAuthorized } = useAuthStore();
  const [isSaving, setIsSaving] = useState(false);

  const formatProjectStatus = (status: string) => {
    const statusInfo = isProjectStatusDto(status) ? PROJECT_STATUS_LABELS[status] : status;
    const chipColor: 'default' | 'success' | 'warning' = 'default';
    return <Chip label={statusInfo} color={chipColor} size="small" sx={{ ml: 1 }} />;
  };

  const deleteBlocked = !!deleteDisabledReason;

  /** `onDelete` runs `useDeleteEntity().deleteEntity`, which already shows the delete confirmation dialog. */
  const handleDelete = () => {
    if (deleteBlocked) return;
    onDelete();
  };

  const handleCancel = async () => {
    // Call the parent's cancel function to reload data from server
    onCancel();
  };

  /**
   * Keep the save action single-flight so users cannot trigger overlapping
   * requests and so the UI reflects backend progress.
   */
  const handleSave = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      await Promise.resolve(onSave());
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2, backgroundColor: 'background.paper' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        {/* Left side - Status and metadata */}
        <Box sx={{ flex: 1, mr: 2, minWidth: 0 }}>
          <Typography variant="body1" component="div" sx={{ fontSize: '1rem' }}>
            {lastChangedDate ? (
              <>
                Last modification on <strong>{formatDate(lastChangedDate)}</strong> by <strong>{lastChangedBy}</strong>
                .&nbsp;
              </>
            ) : null}
            Project Status: {formatProjectStatus(projectStatus)}
          </Typography>

          {/* When user has edits but required fields are missing, explain why save may fail */}
          {!isReadOnly && hasChanges && !isFormValid && validationIssues.length > 0 ? (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              Cannot save yet: {validationIssues.join(', ')}.
            </Typography>
          ) : null}
        </Box>

        {/* Right side - Action buttons + optional delete explanation */}
        <Stack alignItems="flex-end" spacing={0.75}>
          <Stack direction="row" spacing={1}>
            {/* 1. If Readonly → show Edit button */}
            {isReadOnly && isAuthorized(privilege, 'update') ? (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Edit />}
                size="large"
                onClick={() => setIsReadOnly(false)}
                disabled={isSaving}
              >
                Edit Details
              </Button>
            ) : null}

            {/* 2. If Modified → show Save button */}
            {!isReadOnly && hasChanges ? (
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !isFormValid}
                title={
                  !isFormValid && validationIssues.length > 0
                    ? `Missing required fields: ${validationIssues.join(', ')}`
                    : undefined
                }
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            ) : null}

            {/* 3. If NOT readonly → show Cancel button */}
            {!isReadOnly ? (
              <Button variant="contained" color="error" size="large" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
            ) : null}

            {/* 4. Show Delete button only if not in edit mode */}
            {isReadOnly && isAuthorized(privilege, 'delete') ? (
              <Button
                variant="contained"
                color="error"
                size="large"
                onClick={handleDelete}
                disabled={deleteBlocked}
                title={deleteBlocked ? deleteDisabledReason : undefined}
              >
                Delete Project
              </Button>
            ) : null}
          </Stack>
          {isReadOnly && isAuthorized(privilege, 'delete') && deleteBlocked && deleteDisabledReason ? (
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 520, textAlign: 'right' }}>
              {deleteDisabledReason}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};

export default ProjectFooter;
